// v2 tree farm — a broadleaf coppice: nine round-crowned trees on planting mounds
// in a 3x3 grid (two-tone blob canopies, deterministic size variety), two mown
// walking lanes between the rows, a staked nursery row of saplings along the
// south edge, squat stone boundary markers, and a log pile with a chopping block
// in the north-east corner. Footprint-native 4x4. Ported 1:1 from age-of-mcp's
// v2/tree_farm.ts. The model library has no terrain, so the plantation is flat
// (y = 0 is the floor): the source's terrain-following `gridSurfaceY` reduces to
// 0 on flat ground, and `warpedGridSlab` reduces to the flat box slab inlined
// here as `soilSlab` (the same vertex soup the flattened file baked).
import * as THREE from "three";
import { defineModel, pace, type Part } from "../../lib";

const SOIL   = "#5c4327";   // plantation earth
const LANE   = "#8a7448";   // mown walking lanes
const MOUND  = "#4d3820";   // planting mounds
const TRUNK  = "#6e4a2a";
const LEAF   = "#4c8a40";   // canopy blobs (two tones)
const LEAFD  = "#37692f";
const STAKE  = "#9a8254";   // nursery stakes
const MARKER = "#8a8a86";   // stone boundary posts
const LOG    = "#7a5630";
const STUMP  = "#5e4226";

// The plot reaches +/-1.92 tiles (just inside the 4x4 footprint).
const HALF = 1.92;
const ROWS = [-1.28, 0, 1.28] as const;     // 3x3 planting grid
const LANES = [-0.64, 0.64] as const;       // walking lanes between the rows

type V = readonly [number, number, number];
function quad(out: number[], p0: V, p1: V, p2: V, p3: V): void {
  out.push(...p0, ...p1, ...p2, ...p0, ...p2, ...p3);
}

// On flat ground the surface height is 0 everywhere.
const surf = (_x: number, _z: number): number => 0;

// The plantation earth — a flat box slab (the flat reduction of the source's
// `warpedGridSlab(HALF, 0.12, SOIL, FLAT, 1)`): top at y = 0, bottom at -0.12.
function soilSlab(): Part {
  const h = HALF, top = 0, yb = -0.12;
  const tSW: V = [-h, top, -h], tSE: V = [h, top, -h];
  const tNW: V = [-h, top,  h], tNE: V = [h, top,  h];
  const bSW: V = [-h, yb, -h], bSE: V = [h, yb, -h];
  const bNW: V = [-h, yb,  h], bNE: V = [h, yb,  h];
  const pos: number[] = [];
  quad(pos, tSW, tNW, tNE, tSE);   // top    (+Y)
  quad(pos, bSW, bSE, bNE, bNW);   // bottom (-Y)
  quad(pos, tNW, bNW, bNE, tNE);   // north  (+Z)
  quad(pos, tSE, bSE, bSW, tSW);   // south  (-Z)
  quad(pos, tSE, tNE, bNE, bSE);   // east   (+X)
  quad(pos, tSW, bSW, bNW, tNW);   // west   (-X)
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  return { geometry, color: SOIL, position: [0, 0, 0] };
}

// The two mown lanes the forester walks, riding the surface.
function lanes(): Part[] {
  return LANES.map((z) => ({
    geometry: new THREE.BoxGeometry(3.6, 0.03, 0.3),
    color: LANE,
    position: [0, surf(0, z) + 0.03, z] as [number, number, number],
  }));
}

// A planting mound under every tree spot.
function mounds(): Part[] {
  const out: Part[] = [];
  for (const x of ROWS) {
    for (const z of ROWS) {
      out.push({ geometry: new THREE.CylinderGeometry(0.17, 0.2, 0.06, 6), color: MOUND,
        position: [x, surf(x, z) + 0.05, z] });
    }
  }
  return out;
}

// A staked sapling: a whippy stem, one small leaf blob, and its support stake.
function sapling(x: number, z: number): Part[] {
  const y0 = surf(x, z) + 0.06;
  return [
    { geometry: new THREE.CylinderGeometry(0.018, 0.025, 0.22, 5), color: TRUNK,
      position: [x, y0 + 0.11, z] },
    { geometry: new THREE.IcosahedronGeometry(0.1, 0), color: LEAF,
      position: [x, y0 + 0.28, z] },
    { geometry: new THREE.BoxGeometry(0.022, 0.36, 0.022), color: STAKE,
      position: [x + 0.07, y0 + 0.18, z] },
  ];
}

// A young tree: a short trunk under a single rounded blob.
function youngTree(x: number, z: number): Part[] {
  const y0 = surf(x, z) + 0.06;
  return [
    { geometry: new THREE.CylinderGeometry(0.04, 0.055, 0.34, 5), color: TRUNK,
      position: [x, y0 + 0.17, z] },
    { geometry: new THREE.IcosahedronGeometry(0.22, 0), color: LEAF,
      position: [x, y0 + 0.5, z] },
  ];
}

// A mature broadleaf: a trunk under a fat two-blob canopy. `ix`/`iz` give a
// deterministic per-spot size and tone so the grove never reads stamped.
function matureTree(x: number, z: number, ix: number, iz: number): Part[] {
  const y0 = surf(x, z) + 0.06;
  const k = ((ix * 3 + iz) % 3) * 0.025;          // 0 / 0.025 / 0.05 size spread
  const tone = (ix + iz) % 2 === 0 ? LEAF : LEAFD;
  const r = 0.32 + k;
  return [
    { geometry: new THREE.CylinderGeometry(0.055, 0.075, 0.56, 5), color: TRUNK,
      position: [x, y0 + 0.28, z] },
    { geometry: new THREE.IcosahedronGeometry(r, 0), color: tone,
      position: [x, y0 + 0.82, z] },
    { geometry: new THREE.IcosahedronGeometry(r * 0.62, 0), color: tone === LEAF ? LEAFD : LEAF,
      position: [x, y0 + 0.82 + r * 0.78, z] },
  ];
}

function grove(build: (x: number, z: number, ix: number, iz: number) => Part[]): Part[] {
  const out: Part[] = [];
  ROWS.forEach((x, ix) => {
    ROWS.forEach((z, iz) => out.push(...build(x, z, ix, iz)));
  });
  return out;
}

// The staked nursery row along the south edge — next season's planting.
function nurseryRow(): Part[] {
  const out: Part[] = [];
  for (const x of [-0.96, -0.32, 0.32, 0.96]) out.push(...sapling(x, -1.62));
  return out;
}

// Squat stone boundary markers at the corners and edge midpoints.
function markers(): Part[] {
  const e = 1.8;
  const spots: [number, number][] = [
    [-e, -e], [e, -e], [-e, e], [e, e], [-e, 0], [e, 0], [0, -e], [0, e],
  ];
  return spots.map(([x, z]) => ({
    geometry: new THREE.BoxGeometry(0.09, 0.24, 0.09),
    color: MARKER,
    position: [x, surf(x, z) + 0.12, z] as [number, number, number],
  }));
}

// Harvested timber: a small log pyramid and the chopping block beside it.
function logPile(): Part[] {
  const x = 1.52, z = 1.52;
  const y0 = surf(x, z) + 0.04;
  const log = (lz: number, ly: number): Part => ({
    geometry: new THREE.CylinderGeometry(0.075, 0.075, 0.55, 6),
    color: LOG,
    position: [x, ly, lz] as [number, number, number],
    rotation: [0, 0, Math.PI / 2],
  });
  return [
    log(z - 0.08, y0 + 0.075), log(z + 0.08, y0 + 0.075), log(z, y0 + 0.21),
    { geometry: new THREE.CylinderGeometry(0.09, 0.1, 0.16, 6), color: STUMP,
      position: [1.05, y0 + 0.08, z] },
  ];
}

function stage(s: number): Part[] {
  if (s <= 0) return [soilSlab(), ...lanes(), ...mounds()];
  if (s === 1) {
    return [soilSlab(), ...lanes(), ...mounds(),
      ...grove((x, z) => sapling(x, z))];
  }
  if (s === 2) {
    return [soilSlab(), ...lanes(), ...mounds(),
      ...grove((x, z) => youngTree(x, z))];
  }
  return [
    soilSlab(), ...lanes(), ...mounds(),
    ...grove(matureTree),
    ...nurseryRow(), ...markers(), ...logPile(),
  ];
}

export const treeFarmV2 = defineModel({
  id: "tree-farm-v2",
  tiles: { w: 4, h: 4 },
  batchId: "fable",
  name: "Tree farm (v2)",
  tags: ["building","tree","farm","low-poly","age-of-mcp","fable"],
  summary: "Low-poly tree farm.",
  description: "A timber plantation of planted trees grown for wood.",
  author: "age-of-mcp",
  footprint: 1,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => stage(0) },
    { id: "stage-1", label: "Lower", parts: () => stage(1) },
    { id: "stage-2", label: "Upper", parts: () => stage(2) },
    { id: "complete", label: "Complete", parts: () => stage(3) },
  ],
  animations: [],
  stations: [
    { id: "grove", position: [0, 0, 0], facing: 0, capacity: 6 },
  ],
  interactions: [
    { id: "tend", label: "Tending the saplings", station: "grove", variants: ["complete"], ...pace([[-1.55, -0.64], [1.55, -0.64], [1.55, 0.64], [-1.55, 0.64]]) },
  ],
});
